/**
 * Project Hub structure dropdown options — keyed by stable structure id with
 * current display names.
 *
 * Files tracks store denormalized `structureIds` + `structureNames`. Those names
 * go stale when a barn is renamed on the proposal, and designers cannot read
 * `proposalBuilders`, so the Files UI used to label options from the parallel
 * name arrays alone. Building options by id (proposal name first, then any
 * stream’s name for that id) keeps each active building once with the right label.
 *
 * After tracks are tagged, untagged leftover proposal structures (the pre-split
 * whole-project default) are dropped — same idea as {@link rfqStructureNodeSets}.
 */

import type { ProjectFileStream } from '../types';
import type { StructureRef } from './proposalStructureRefs';
import { taggedStructureIds } from './rfqProjectStructures';

type StreamStructureTags = Pick<ProjectFileStream, 'id' | 'structureIds' | 'structureNames'>;

/** Pair each structure id with the best known display name (proposal wins). */
export function structureNameById(
  proposalStructures: ReadonlyArray<StructureRef> | null | undefined,
  streams: ReadonlyArray<Pick<ProjectFileStream, 'structureIds' | 'structureNames'>> | null | undefined,
): Map<string, string> {
  const names = new Map<string, string>();
  for (const s of proposalStructures ?? []) {
    const id = (s.id || '').trim();
    if (!id) continue;
    const name = (s.name || '').trim();
    names.set(id, name || id);
  }
  for (const stream of streams ?? []) {
    (stream.structureIds ?? []).forEach((rawId, i) => {
      const id = (rawId || '').trim();
      if (!id || names.has(id)) return;
      const n = (stream.structureNames?.[i] || '').trim();
      if (n) names.set(id, n);
    });
  }
  return names;
}

/**
 * Dropdown / rail options for Project Hub Files.
 *
 * - Keyed by structure id (deduped).
 * - Labels prefer live proposal names, then denormalized stream names for that id.
 * - When any track is tagged, only tagged structures are listed (drops pre-split leftovers).
 * - When the proposal list is empty (e.g. designer role cannot read proposals), options
 *   come from unique tagged ids on streams in first-seen order.
 */
export function buildHubStructureOptions(
  proposalStructures: ReadonlyArray<StructureRef> | null | undefined,
  streams: ReadonlyArray<Pick<ProjectFileStream, 'structureIds' | 'structureNames'>> | null | undefined,
): StructureRef[] {
  const proposal = (proposalStructures ?? []).filter(s => !!(s.id || '').trim());
  const nameById = structureNameById(proposal, streams);
  const tagged = taggedStructureIds(streams);

  let ids: string[];
  if (tagged && tagged.size > 0) {
    const fromProposal = proposal.map(s => s.id.trim()).filter(id => tagged.has(id));
    if (fromProposal.length > 0) {
      const seen = new Set(fromProposal);
      const extras: string[] = [];
      Array.from(tagged).forEach(id => {
        if (seen.has(id) || !nameById.has(id)) return;
        seen.add(id);
        extras.push(id);
      });
      ids = [...fromProposal, ...extras];
    } else {
      ids = [];
      const seen = new Set<string>();
      for (const stream of streams ?? []) {
        for (const raw of stream.structureIds ?? []) {
          const id = (raw || '').trim();
          if (!id || seen.has(id) || !nameById.has(id)) continue;
          seen.add(id);
          ids.push(id);
        }
      }
    }
  } else {
    ids = proposal.map(s => s.id.trim()).filter(Boolean);
  }

  return ids.map(id => ({ id, name: nameById.get(id) || id }));
}

export interface StreamStructureNamePatch {
  streamId: string;
  structureIds: string[];
  structureNames: string[];
}

/**
 * Patches to rewrite stale denormalized `structureNames` on tracks so they match
 * proposal names for the same structure ids. Empty when already in sync.
 */
export function streamStructureNameSyncPatches(
  streams: ReadonlyArray<StreamStructureTags> | null | undefined,
  proposalStructures: ReadonlyArray<StructureRef> | null | undefined,
): StreamStructureNamePatch[] {
  const nameById = new Map<string, string>();
  for (const s of proposalStructures ?? []) {
    const id = (s.id || '').trim();
    if (!id) continue;
    const name = (s.name || '').trim();
    if (name) nameById.set(id, name);
  }
  if (nameById.size === 0) return [];

  const patches: StreamStructureNamePatch[] = [];
  for (const stream of streams ?? []) {
    const streamId = (stream.id || '').trim();
    const ids = (stream.structureIds ?? []).map(id => (id || '').trim()).filter(Boolean);
    if (!streamId || ids.length === 0) continue;
    let dirty = false;
    const names = ids.map((id, i) => {
      const canonical = nameById.get(id);
      const current = (stream.structureNames?.[i] || '').trim();
      if (canonical && canonical !== current) {
        dirty = true;
        return canonical;
      }
      return current || canonical || id;
    });
    if (dirty) patches.push({ streamId, structureIds: ids, structureNames: names });
  }
  return patches;
}
